// Steps C and the acceptance half of D (Session BDB): a fixed price quotation for the chosen tests, the Gmail draft the office sends,
// and the customer's answer recorded by a person. The price is worked out by the server from the confirmed day rates and is never
// typed. Test weights, parts, specialist attendance and travel are "on quotation": the office records what it agreed as extras.
// Nothing here sends anything.
import { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import { formatDate } from "../../../../lib/format";
import { ConfirmButton } from "../../../../components/ConfirmButton";
import type { SafedQuotePlanView, SafedQuoteView, SafedRecommendationView } from "../../../../lib/safedFlowTypes";
import { btnCls, cardCls, errorText, inputCls, poundsFromPence, quietBtnCls } from "./safedUi";

interface Props {
  assetId: string;
  quotes: SafedQuoteView[];
  fromRecommendation: SafedRecommendationView | null;
  onClearRecommendation: () => void;
  onChanged: () => void | Promise<void>;
}

const STATUS_TEXT: Record<SafedQuoteView["status"], string> = {
  draft: "Not yet drafted",
  drafted: "Draft made, waiting for the customer",
  accepted: "Accepted",
  declined: "Declined",
};
const VIA_TEXT: Record<string, string> = { email: "email", phone: "phone", in_writing: "in writing", other: "another way" };

export function SafedQuotes({ assetId, quotes, fromRecommendation, onClearRecommendation, onChanged }: Props) {
  const [creating, setCreating] = useState(false);
  const [catalogue, setCatalogue] = useState<{ id: string; name: string; cls: "STANDARD" | "STRESS" }[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [dayType, setDayType] = useState<"full_day" | "half_day">("full_day");
  const [engineers, setEngineers] = useState<1 | 2>(1);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [plan, setPlan] = useState<SafedQuotePlanView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [answering, setAnswering] = useState<{ id: string; byName: string; via: "email" | "phone" | "in_writing" | "other"; note: string } | null>(null);
  const [extrasFor, setExtrasFor] = useState<{ id: string; rows: { description: string; pounds: string }[] } | null>(null);
  const [draftPreview, setDraftPreview] = useState<{ id: string; to: string | null; subject: string } | null>(null);

  useEffect(() => {
    if (fromRecommendation) {
      setCreating(true);
      setPicked(fromRecommendation.tests.map((t) => t.id));
    }
  }, [fromRecommendation]);

  useEffect(() => {
    if (!creating || catalogue.length > 0) return;
    api.safedJobs
      .tests(assetId)
      .then((t) => setCatalogue(t.tests.map((x) => ({ id: x.id, name: x.name, cls: x.cls }))))
      .catch(() => setError("Could not load the tests for this lift."));
  }, [creating, assetId, catalogue.length]);

  const body = () => ({
    assetId,
    testIds: picked,
    dayType,
    engineers,
    recipientEmail: recipientEmail.trim() || null,
    recommendationId: fromRecommendation?.id ?? null,
  });

  async function showPrice() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.safedFlow.quotePreview(body());
      setPlan(res.plan);
    } catch (err) {
      setPlan(null);
      setError(errorText(err, "Could not price that."));
    } finally {
      setBusy(false);
    }
  }

  async function create() {
    setBusy(true);
    setError(null);
    try {
      const res = await api.safedFlow.quoteCreate(body());
      setCreating(false);
      setPlan(null);
      setPicked([]);
      onClearRecommendation();
      setNotice(`Quotation ${res.reference} created. Nothing has been sent.`);
      await onChanged();
    } catch (err) {
      setError(errorText(err, "Could not create the quotation."));
    } finally {
      setBusy(false);
    }
  }

  async function previewDraft(id: string) {
    setError(null);
    try {
      const res = await api.safedFlow.quoteDraft(id, true);
      if ("to" in res) setDraftPreview({ id, to: res.to, subject: res.subject });
    } catch (err) {
      setError(errorText(err, "Could not prepare the draft."));
    }
  }

  async function makeDraft(id: string) {
    setError(null);
    try {
      const res = await api.safedFlow.quoteDraft(id, false);
      setDraftPreview(null);
      if (!res.preview) setNotice(`A Gmail draft to ${res.to} is ready for you to read and send.`);
      await onChanged();
    } catch (err) {
      setError(errorText(err, "Could not make the Gmail draft."));
    }
  }

  async function saveAnswer(kind: "accepted" | "declined" | "cleared", q: SafedQuoteView) {
    setError(null);
    try {
      await api.safedFlow.quoteAnswer(q.id, kind === "accepted" && answering ? { answer: kind, byName: answering.byName, via: answering.via, note: answering.note || null } : { answer: kind });
      setAnswering(null);
      await onChanged();
    } catch (err) {
      setError(errorText(err, "Could not record that."));
    }
  }

  async function saveExtras() {
    if (!extrasFor) return;
    setError(null);
    try {
      const rows = extrasFor.rows
        .filter((r) => r.description.trim() || r.pounds.trim())
        .map((r) => ({ description: r.description.trim(), amountPence: Math.round(Number(r.pounds) * 100) }));
      await api.safedFlow.quoteExtras(extrasFor.id, rows);
      setExtrasFor(null);
      await onChanged();
    } catch (err) {
      setError(errorText(err, "Could not save the extras. Each needs a description and an amount."));
    }
  }

  return (
    <section aria-label="SAFed quotations" className="mt-4">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="font-heading text-sm uppercase tracking-wide text-ele-strong">Fixed price quotations</h3>
        <button type="button" className={btnCls} onClick={() => setCreating(true)}>New quotation</button>
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-ele-danger">{error}</p>}
      {notice && <p role="status" className="mt-2 text-sm text-ele-body">{notice}</p>}

      {creating && (
        <div className={`mt-3 ${cardCls}`}>
          {fromRecommendation && <p className="text-sm text-ele-body">From the recommendation: {fromRecommendation.sourceLabel}{fromRecommendation.documentRef ? `, ${fromRecommendation.documentRef}` : ""}.</p>}
          <ul data-vscroll className="mt-1 max-h-56 space-y-1 overflow-auto">
            {catalogue.map((t) => (
              <li key={t.id}>
                <label className="flex min-h-10 items-center gap-2 text-sm text-ele-strong">
                  <input type="checkbox" checked={picked.includes(t.id)} onChange={(e) => { setPlan(null); setPicked(e.target.checked ? [...picked, t.id] : picked.filter((p) => p !== t.id)); }} />
                  {t.id} {t.name}{t.cls === "STRESS" ? " (stress test, two engineers)" : ""}
                </label>
              </li>
            ))}
          </ul>
          <label className="mt-2 block text-sm text-ele-body">
            Length of day
            <select className={inputCls} value={dayType} onChange={(e) => { setPlan(null); setDayType(e.target.value as typeof dayType); }}>
              <option value="full_day">Full day</option>
              <option value="half_day">Half day</option>
            </select>
          </label>
          <label className="mt-2 block text-sm text-ele-body">
            Engineers
            <select className={inputCls} value={engineers} onChange={(e) => { setPlan(null); setEngineers(Number(e.target.value) === 2 ? 2 : 1); }}>
              <option value={1}>One</option>
              <option value={2}>Two</option>
            </select>
          </label>
          <label className="mt-2 block text-sm text-ele-body">
            Customer email for the draft (the customer&apos;s billing email is used if left blank)
            <input className={inputCls} value={recipientEmail} onChange={(e) => setRecipientEmail(e.target.value)} />
          </label>
          {plan && (
            <div className="mt-3 rounded-ele border border-ele-green px-3 py-2 text-sm text-ele-strong">
              <p>
                {plan.dayLabel}, {plan.engineers === 2 ? "two engineers" : "one engineer"}: <strong>{poundsFromPence(plan.amountPence)}</strong> ex VAT, fixed.
              </p>
              <p className="mt-1 text-ele-body">On quotation, not in that price: {plan.extraLabels.join("; ")}.</p>
              {plan.notes.map((n) => (
                <p key={n} className="mt-1 text-xs text-ele-muted">{n}</p>
              ))}
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={btnCls} disabled={busy || picked.length === 0} onClick={() => void showPrice()}>Show the price</button>
            <button type="button" className={btnCls} disabled={busy || !plan} onClick={() => void create()}>Create the quotation</button>
            <button type="button" className={quietBtnCls} onClick={() => { setCreating(false); setPlan(null); onClearRecommendation(); }}>Cancel</button>
          </div>
        </div>
      )}

      {quotes.length === 0 && !creating && <p className="mt-2 text-sm text-ele-muted">No SAFed quotation for this lift yet.</p>}
      <ul className="mt-2 space-y-2">
        {quotes.map((q) => (
          <li key={q.id} className={`${cardCls} text-sm`}>
            <p className="text-ele-strong">
              {q.reference} | {poundsFromPence(q.amountPence)} ex VAT | {q.dayType === "full_day" ? "full day" : "half day"}, {q.engineers === 2 ? "two engineers" : "one engineer"}
            </p>
            <p className="text-ele-body">Tests: {q.tests.map((t) => t.id).join(", ")}</p>
            <p className="text-ele-body">
              {STATUS_TEXT[q.status]}
              {q.acceptedAt ? ` on ${formatDate(q.acceptedAt)} by ${q.acceptedByName ?? ""}, by ${VIA_TEXT[q.acceptedVia ?? "other"] ?? "another way"}` : ""}
              {q.declinedAt ? ` on ${formatDate(q.declinedAt)}` : ""}
            </p>
            {q.agreedExtras.length > 0 && (
              <p className="text-ele-body">Agreed extras: {q.agreedExtras.map((e) => `${e.description} ${poundsFromPence(e.amountPence)}`).join("; ")}</p>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              <a className={`${btnCls} inline-flex items-center`} href={`/api/safed-flow/quotes/${q.id}/quotation.pdf`} target="_blank" rel="noreferrer">Quotation PDF</a>
              {q.status !== "declined" && <button type="button" className={btnCls} onClick={() => void previewDraft(q.id)}>Make the Gmail draft</button>}
              {q.status !== "accepted" && q.status !== "declined" && (
                <button type="button" className={btnCls} onClick={() => setAnswering({ id: q.id, byName: "", via: "email", note: "" })}>Customer accepted</button>
              )}
              {q.status !== "accepted" && q.status !== "declined" && (
                <ConfirmButton label="Customer declined" confirmLabel="Confirm: declined" className={quietBtnCls} onConfirm={() => void saveAnswer("declined", q)} />
              )}
              {(q.status === "accepted" || q.status === "declined") && (
                <ConfirmButton label="Clear the answer" confirmLabel="Confirm: clear it" className={quietBtnCls} onConfirm={() => void saveAnswer("cleared", q)} />
              )}
              {q.status !== "declined" && (
                <button type="button" className={btnCls} onClick={() => setExtrasFor({ id: q.id, rows: q.agreedExtras.length > 0 ? q.agreedExtras.map((e) => ({ description: e.description, pounds: (e.amountPence / 100).toFixed(2) })) : [{ description: "", pounds: "" }] })}>
                  Agreed extras
                </button>
              )}
              {q.jobIds.length === 0 && (
                <ConfirmButton
                  label="Delete"
                  confirmLabel="Confirm: delete it"
                  className="min-h-11 px-3 font-menu text-xs uppercase tracking-wide text-ele-danger"
                  onConfirm={() => void api.safedFlow.deleteQuote(q.id).then(() => onChanged()).catch((e) => setError(errorText(e, "Could not delete it.")))}
                />
              )}
            </div>

            {draftPreview?.id === q.id && (
              <div className="mt-2 rounded-ele border border-ele-green px-3 py-2">
                <p className="text-ele-strong">{draftPreview.to ? `A draft to ${draftPreview.to} with the quotation attached.` : "There is no email address for this quotation yet."}</p>
                <p className="text-ele-body">Subject: {draftPreview.subject}. It goes into Gmail as a draft. You read it and send it.</p>
                <div className="mt-2 flex gap-2">
                  <button type="button" className={btnCls} disabled={!draftPreview.to} onClick={() => void makeDraft(q.id)}>Make the draft</button>
                  <button type="button" className={quietBtnCls} onClick={() => setDraftPreview(null)}>Cancel</button>
                </div>
              </div>
            )}

            {answering?.id === q.id && (
              <div className="mt-2 space-y-2">
                <label className="block text-ele-body">
                  Who accepted it for the customer
                  <input className={inputCls} value={answering.byName} onChange={(e) => setAnswering({ ...answering, byName: e.target.value })} />
                </label>
                <label className="block text-ele-body">
                  How it reached us
                  <select className={inputCls} value={answering.via} onChange={(e) => setAnswering({ ...answering, via: e.target.value as typeof answering.via })}>
                    <option value="email">Email</option>
                    <option value="phone">Phone</option>
                    <option value="in_writing">In writing</option>
                    <option value="other">Another way</option>
                  </select>
                </label>
                <label className="block text-ele-body">
                  Note (optional)
                  <input className={inputCls} value={answering.note} onChange={(e) => setAnswering({ ...answering, note: e.target.value })} />
                </label>
                <div className="flex gap-2">
                  <button type="button" className={btnCls} disabled={answering.byName.trim().length < 2} onClick={() => void saveAnswer("accepted", q)}>Record it as accepted</button>
                  <button type="button" className={quietBtnCls} onClick={() => setAnswering(null)}>Cancel</button>
                </div>
              </div>
            )}

            {extrasFor?.id === q.id && (
              <div className="mt-2 space-y-2">
                <p className="text-xs text-ele-muted">Only what you agreed with the customer. These are billed with the fixed price.</p>
                {extrasFor.rows.map((r, i) => (
                  <div key={i} className="flex flex-wrap gap-2">
                    <input aria-label="Extra description" className={`${inputCls} flex-1`} placeholder="What it is" value={r.description} onChange={(e) => setExtrasFor({ ...extrasFor, rows: extrasFor.rows.map((x, j) => (j === i ? { ...x, description: e.target.value } : x)) })} />
                    <input aria-label="Extra amount in pounds, ex VAT" className={`${inputCls} w-32`} placeholder="Pounds ex VAT" inputMode="decimal" value={r.pounds} onChange={(e) => setExtrasFor({ ...extrasFor, rows: extrasFor.rows.map((x, j) => (j === i ? { ...x, pounds: e.target.value } : x)) })} />
                  </div>
                ))}
                <div className="flex gap-2">
                  <button type="button" className={quietBtnCls} onClick={() => setExtrasFor({ ...extrasFor, rows: [...extrasFor.rows, { description: "", pounds: "" }] })}>Add a line</button>
                  <button type="button" className={btnCls} onClick={() => void saveExtras()}>Save the extras</button>
                  <button type="button" className={quietBtnCls} onClick={() => setExtrasFor(null)}>Cancel</button>
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
